// ═══════════════════════════════════════════════════════════════
// GeoOps — Full-screen Map Editor with drawing tools,
// data layers (MainStreet, Shelters, Impact), and quick actions
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Group, Button, Paper, Text, Stack, Badge, ActionIcon,
  Center, Loader, ThemeIcon, Title, Tooltip, TextInput,
  Switch, Divider, Modal, Textarea, Select, SegmentedControl,
} from '@mantine/core';
import {
  IconPolygon, IconLine, IconCircle, IconX, IconArrowLeft,
  IconAlertTriangle, IconSearch, IconBuilding,
  IconPhone, IconMail, IconClipboardCheck,
  IconLayersLinked, IconList, IconRoute, IconMapPin, IconRefresh,
} from '@tabler/icons-react';
import { MapContainer, TileLayer, LayersControl, CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import * as L from 'leaflet';
import { useNavigate } from 'react-router-dom';
import { searchAddress, type AddressResult } from '../../utils/perimeterOverpass';
import { notifications } from '@mantine/notifications';
import { useAuthStore } from '../../store/authStore';
import { useGeoOpsStore } from '../../store/geoopsStore';
import type { GeoOpsZone } from '../../store/geoopsStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import ZoneMapOverlay from '../../components/map/ZoneMapOverlay';
import DrawingController, { type DrawMode, type LiveMeasurement } from './DrawingController';
import { formatDistance, formatArea } from '../../lib/geoopsGeo';
import IncidentLocationMarker from '../../components/map/IncidentLocationMarker';
import ZoneListPanel from './ZoneListPanel';
import ZoneForm from './ZoneForm';
import { zoneBounds, isInsideZone } from '../../lib/geoopsGeo';
import { formatPhone } from '../../lib/phoneFormat';

// Data layer stores
import { useMainStreetStore, type MSOrganization } from '../../store/mainstreetStore';
import { useMainStreetOpsStore } from '../../store/mainstreetOpsStore';
import { useShelterStore } from '../../store/shelterStore';
import { useImpactStore } from '../../store/impactStore';
import { SECTOR_COLORS } from '../mainstreet/constants';
import { SHELTER_STATUS_COLORS } from '../../types/shelter';
import { WORKER_URL } from '../../lib/api-switch';

import 'leaflet/dist/leaflet.css';

// ── Helpers ─────────────────────────────────────────────────

function parseCoords(coordStr: string): { lat: number; lng: number } | null {
  if (!coordStr) return null;
  const parts = coordStr.split(',').map(s => parseFloat(s.trim()));
  if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return { lat: parts[0], lng: parts[1] };
  return null;
}

function FitToZone({ geometry }: { geometry: GeoJSON.Geometry | null }) {
  const map = useMap();
  useEffect(() => {
    if (!geometry) return;
    const bounds = zoneBounds(geometry);
    if (bounds) map.fitBounds(bounds, { padding: [40, 40] });
  }, [geometry, map]);
  return null;
}

// Disables Leaflet's built-in double-click zoom while drawing so the map
// doesn't zoom when the user double-clicks to finish a shape.
function MapDrawControls({ drawMode }: { drawMode: DrawMode }) {
  const map = useMap();
  useEffect(() => {
    if (drawMode !== 'idle') {
      map.doubleClickZoom.disable();
    } else {
      map.doubleClickZoom.enable();
    }
  }, [drawMode, map]);
  // Also swallow dblclick at the Leaflet event level while drawing
  useMapEvents({
    dblclick: (e) => {
      if (drawMode !== 'idle') {
        e.originalEvent.preventDefault();
        e.originalEvent.stopPropagation();
      }
    },
  });
  return null;
}

const SEVERITY_COLORS: Record<string, string> = {
  affected: '#16a34a', minor: '#d97706', major: '#ea580c', destroyed: '#dc2626',
};

export default function GeoOpsMap() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { zones, isLoading, loadZones, saveZone, updateZone, deleteZone } = useGeoOpsStore();
  const { canEdit } = useModulePermission('geoops');

  const [drawMode, setDrawMode] = useState<DrawMode>('idle');
  const [drawnGeometry, setDrawnGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [liveMeasure, setLiveMeasure] = useState<LiveMeasurement | null>(null);
  // Buffer radius (meters) for corridor mode — default 0.25 mi (~402 m)
  const [corridorBufferM, setCorridorBufferM] = useState<number>(402);
  const [showZoneForm, setShowZoneForm] = useState(false);
  const [editingZone, setEditingZone] = useState<GeoOpsZone | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [hiddenZoneIds, setHiddenZoneIds] = useState<Set<number>>(new Set());
  const [fitGeometry, setFitGeometry] = useState<GeoJSON.Geometry | null>(null);

  // Address search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AddressResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const mapRef = useRef<L.Map | null>(null);
  const mapWrapperRef = useRef<HTMLDivElement>(null);

  // Data layers
  const [showBusinesses, setShowBusinesses] = useState(false);
  const [showShelters, setShowShelters] = useState(false);
  const [showAssessments, setShowAssessments] = useState(false);
  const [showPois, setShowPois] = useState(false);
  const [showLayerPanel, setShowLayerPanel] = useState(true);

  // POI (HERE Browse) state
  interface POI {
    id: string;
    title: string;
    lat: number;
    lng: number;
    address?: string;
    phone?: string;
    categoryId?: string;
    categoryName?: string;
    /** All categories returned by HERE, in original order, for transparency */
    allCategories?: Array<{ id: string; name: string; primary?: boolean }>;
  }
  const [pois, setPois] = useState<POI[]>([]);
  const [poiLoading, setPoiLoading] = useState(false);
  // HERE category IDs — narrowed to EM-useful categories only.
  //   100 = Eat & Drink (food access for displaced residents)
  //   350 = Transport (gas stations, charging, transit hubs)
  //   400 = Accommodation (hotels for evacuees)
  //   500 = Leisure & Outdoor (parks, sports complexes — staging areas)
  //   550 = Admin / Buildings (hospitals, schools, gov't, places of worship)
  //   800 = Facilities (pharmacies, ATMs, post offices, libraries)
  //   900 = Services (auto repair, healthcare services, beauty)
  // Deliberately excludes 200 (nightlife/bars) and 700 (general retail —
  // includes vape shops, cosmetics, etc. that aren't useful for EM ops)
  // and 600 (natural/geographical — mountains, rivers).
  const poiCategoryFilter = '100-1000-0000,350-3500-0000,400-4000-0000,500-5000-0000,550-5500-0000,800-8000-0000,900-9000-0000';

  // Generic single HERE Browse call → list of POIs
  const fetchPoisAt = useCallback(async (lat: number, lng: number, radiusM: number): Promise<POI[]> => {
    const params = new URLSearchParams({
      at: `${lat},${lng}`,
      in: `circle:${lat},${lng};r=${Math.round(radiusM)}`,
      categories: poiCategoryFilter,
      limit: '100',
    });
    const r = await fetch(`${WORKER_URL}/api/here/browse?${params}`, {
      headers: { 'Origin': 'https://apps.opengov.com' },
    });
    if (!r.ok) throw new Error(`HERE Browse ${r.status}`);
    const data = await r.json();
    const items = (data.items || []) as Array<{
      id: string; title: string;
      position: { lat: number; lng: number };
      address?: { label?: string };
      contacts?: Array<{ phone?: Array<{ value: string }> }>;
      categories?: Array<{ id: string; name: string; primary?: boolean }>;
    }>;
    return items.map(it => {
      // HERE returns multiple categories per POI in non-obvious order. We
      // pick a display label by preferring (in order):
      //   1. The category flagged primary: true
      //   2. The first category whose ID is NOT in a noise-prone group
      //      (religious places, cemeteries) — which often appear at index 0
      //      even for unrelated POIs (e.g. a fitness center inside a church
      //      property gets labeled as Religious Place)
      //   3. The first category as last resort
      const cats = it.categories || [];
      const noisePrefixes = [
        '300-3100', // Religious Place (older taxonomy)
        '550-5520', // Religious Place (newer taxonomy)
        '550-5560', // Cemetery
      ];
      const isNoise = (id?: string) =>
        !!id && noisePrefixes.some(p => id.startsWith(p));

      const primaryCat = cats.find(c => c.primary === true);
      const nonNoiseCat = cats.find(c => !isNoise(c.id));
      const cat = primaryCat || nonNoiseCat || cats[0];

      return {
        id: it.id,
        title: it.title,
        lat: it.position.lat,
        lng: it.position.lng,
        address: it.address?.label,
        phone: it.contacts?.[0]?.phone?.[0]?.value,
        categoryId: cat?.id,
        categoryName: cat?.name,
        allCategories: cats,
      };
    });
  }, [poiCategoryFilter]);

  const fetchPoisInBounds = useCallback(async () => {
    if (!mapRef.current) return;
    const bounds = mapRef.current.getBounds();
    const center = bounds.getCenter();
    const radiusM = Math.min(
      Math.max(
        center.distanceTo(bounds.getNorthEast()),
        center.distanceTo(bounds.getSouthWest()),
      ),
      20000,
    );
    setPoiLoading(true);
    try {
      const newPois = await fetchPoisAt(center.lat, center.lng, radiusM);
      setPois(newPois);
    } catch (err) {
      notifications.show({
        message: err instanceof Error ? err.message : 'POI fetch failed',
        color: 'red',
      });
    } finally {
      setPoiLoading(false);
    }
  }, [fetchPoisAt]);

  // Scan ALL zones in the current incident — one HERE Browse call per zone,
  // results merged & deduped by POI id. The existing point-in-polygon filter
  // (poisInZone) then narrows to just those actually inside the polygons.
  // Reads zones lazily from the store at click time to avoid forward-ref
  // issues with incidentZones (declared further down).
  const scanAllZones = useCallback(async () => {
    const incidentIdStr = String(activeIncident?.id || '');
    const allZones = useGeoOpsStore.getState().zones.filter(z => String(z.incidentId) === incidentIdStr);
    if (allZones.length === 0) {
      notifications.show({ message: 'No zones to scan in this incident', color: 'yellow' });
      return;
    }
    setPoiLoading(true);
    const merged = new Map<string, POI>();
    let scanned = 0;
    let failed = 0;
    try {
      for (const zone of allZones) {
        if (!zone.geometry) continue;
        const bounds = zoneBounds(zone.geometry);
        if (!bounds) continue;
        const center = bounds.getCenter();
        const radiusM = Math.min(
          Math.max(
            center.distanceTo(bounds.getNorthEast()),
            center.distanceTo(bounds.getSouthWest()),
          ),
          20000,
        );
        try {
          const items = await fetchPoisAt(center.lat, center.lng, radiusM);
          for (const p of items) {
            // Dedupe — same POI may appear in multiple overlapping zone scans
            if (!merged.has(p.id)) merged.set(p.id, p);
          }
          scanned++;
        } catch {
          failed++;
        }
      }
      setPois(Array.from(merged.values()));
      notifications.show({
        message: `Scanned ${scanned} zone${scanned !== 1 ? 's' : ''} — ${merged.size} unique POIs found${failed > 0 ? ` (${failed} failed)` : ''}`,
        color: failed > 0 ? 'yellow' : 'green',
      });
    } finally {
      setPoiLoading(false);
    }
  }, [activeIncident, fetchPoisAt]);

  // Auto-fetch on first toggle on
  useEffect(() => {
    if (showPois && pois.length === 0) {
      // Slight delay so the map ref is populated
      setTimeout(() => { fetchPoisInBounds(); }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPois]);

  // Status check modal
  const [showStatusCheckModal, setShowStatusCheckModal] = useState(false);
  const [statusCheckTitle, setStatusCheckTitle] = useState('');
  const [statusCheckQuestion, setStatusCheckQuestion] = useState('Are you currently operational? Please report your status.');
  const [statusCheckCreating, setStatusCheckCreating] = useState(false);

  // Add to list modal
  const [showListModal, setShowListModal] = useState(false);
  const [listMode, setListMode] = useState<'new' | 'existing'>('new');
  const [newListName, setNewListName] = useState('');
  const [newListDesc, setNewListDesc] = useState('');
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [listCreating, setListCreating] = useState(false);

  // Data layer stores
  const { organizations, loadOrganizations, updateOrganization, orgLists, loadOrgLists, createOrgList, updateOrgList } = useMainStreetStore();
  const { createStatusCheck } = useMainStreetOpsStore();
  const { shelters, loadShelters } = useShelterStore();
  const { assessments, loadAssessments } = useImpactStore();

  useEffect(() => {
    if (activeIncident) loadZones(String(activeIncident.id));
  }, [activeIncident]);

  // Compute the initial map center from the active incident's coordinates,
  // falling back to Atlanta if the incident has no lat/lng.
  const incidentCenter = useMemo<[number, number]>(() => {
    const lat = parseFloat(activeIncident?.latitude || '');
    const lng = parseFloat(activeIncident?.longitude || '');
    if (isFinite(lat) && isFinite(lng) && !(lat === 0 && lng === 0)) return [lat, lng];
    return [33.749, -84.388];
  }, [activeIncident]);

  // Recenter the map when the active incident changes (the MapContainer's
  // initial center prop is only honored on first mount).
  useEffect(() => {
    if (!mapRef.current) return;
    const lat = parseFloat(activeIncident?.latitude || '');
    const lng = parseFloat(activeIncident?.longitude || '');
    if (isFinite(lat) && isFinite(lng) && !(lat === 0 && lng === 0)) {
      mapRef.current.setView([lat, lng], 13, { animate: true });
    }
  }, [activeIncident]);

  // Load data layers on toggle
  useEffect(() => { if (showBusinesses) { loadOrganizations(); loadOrgLists(); } }, [showBusinesses]);
  useEffect(() => { if (showShelters) loadShelters(); }, [showShelters]);
  useEffect(() => { if (showAssessments) loadAssessments(); }, [showAssessments]);

  const incidentZones = useMemo(
    () => zones.filter(z => String(z.incidentId) === String(activeIncident?.id)),
    [zones, activeIncident]
  );
  const visibleZones = useMemo(
    () => incidentZones.filter(z => !hiddenZoneIds.has(z.id)),
    [incidentZones, hiddenZoneIds]
  );
  const activeZones = useMemo(() => incidentZones.filter(z => z.status === 'active'), [incidentZones]);

  // Geocoded orgs
  const orgMarkers = useMemo(() => {
    if (!showBusinesses) return [];
    return organizations.map(o => {
      const coords = parseCoords(o.coordinates);
      if (!coords) return null;
      return { org: o, coords };
    }).filter(Boolean) as { org: MSOrganization; coords: { lat: number; lng: number } }[];
  }, [organizations, showBusinesses]);

  // Orgs in active zones
  const orgsInZone = useMemo(() => {
    if (activeZones.length === 0) return new Set<number>();
    const s = new Set<number>();
    orgMarkers.forEach(m => {
      if (activeZones.some(z => z.geometry && isInsideZone(m.coords.lat, m.coords.lng, z.geometry))) {
        s.add(m.org.id);
      }
    });
    return s;
  }, [orgMarkers, activeZones]);

  // POIs caught inside ANY incident zone (not just active) — used for the
  // captured-POIs panel and scan results. Annotates each POI with the names
  // of the zones it falls inside so the panel can group by zone.
  const poisInZone = useMemo(() => {
    if (incidentZones.length === 0 || pois.length === 0) return [] as Array<POI & { zoneNames: string[] }>;
    const out: Array<POI & { zoneNames: string[] }> = [];
    for (const p of pois) {
      const matchedZones: string[] = [];
      for (const z of incidentZones) {
        if (z.geometry && isInsideZone(p.lat, p.lng, z.geometry)) {
          matchedZones.push(z.name || `Zone ${z.id}`);
        }
      }
      if (matchedZones.length > 0) {
        out.push({ ...p, zoneNames: matchedZones });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pois, incidentZones]);

  // Shelter markers
  const shelterMarkers = useMemo(() => {
    if (!showShelters) return [];
    return shelters.filter(s => s.latitude && s.longitude).map(s => ({
      shelter: s, lat: parseFloat(s.latitude), lng: parseFloat(s.longitude),
    })).filter(m => !isNaN(m.lat) && !isNaN(m.lng));
  }, [shelters, showShelters]);

  // Assessment markers
  const assessmentMarkers = useMemo(() => {
    if (!showAssessments) return [];
    return assessments.filter(a => a.latitude && a.longitude);
  }, [assessments, showAssessments]);

  // Address search
  const handleSearchInput = (val: string) => {
    setSearchQuery(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (val.trim().length < 3) { setSearchResults([]); setShowResults(false); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchAddress(val.trim());
        setSearchResults(results);
        setShowResults(true);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 400);
  };

  const handleSelectAddress = (result: AddressResult) => {
    mapRef.current?.setView([parseFloat(result.lat), parseFloat(result.lon)], 15, { animate: true });
    setSearchQuery(result.display_name);
    setShowResults(false);
  };

  // Block dblclick zoom at native DOM level while drawing is active.
  // Runs in capture phase so Leaflet never sees the dblclick event.
  // The two preceding click events still fire normally, completing the shape.
  useEffect(() => {
    const wrapper = mapWrapperRef.current;
    if (!wrapper) return;
    const blockDblClick = (e: MouseEvent) => {
      if (drawMode !== 'idle') {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    wrapper.addEventListener('dblclick', blockDblClick, true);
    return () => wrapper.removeEventListener('dblclick', blockDblClick, true);
  }, [drawMode]);

  const handleDrawComplete = useCallback((geometry: GeoJSON.Geometry) => {
    setDrawnGeometry(geometry);
    setDrawMode('idle');
    setLiveMeasure(null);
    setShowZoneForm(true);
  }, []);

  const handleDrawCancel = useCallback(() => {
    setDrawMode('idle');
    setLiveMeasure(null);
  }, []);

  const handleSaveNewZone = async (formData: any) => {
    if (!activeIncident || !drawnGeometry) return;
    try {
      await saveZone({
        ...formData, incidentId: String(activeIncident.id),
        geometry: drawnGeometry, createdBy: user?.fullName || user?.email || '',
      });
      notifications.show({ message: `Zone "${formData.name}" created`, color: 'green' });
      setDrawnGeometry(null);
      setShowZoneForm(false);
    } catch { notifications.show({ message: 'Failed to save zone', color: 'red' }); }
  };

  const handleEditZone = async (formData: any) => {
    if (!editingZone) return;
    await updateZone(editingZone.id, formData);
    notifications.show({ message: `Zone updated`, color: 'green' });
    setEditingZone(null);
  };

  const handleDeleteZone = async (id: number) => {
    const z = incidentZones.find(z => z.id === id);
    if (!z || !confirm(`Delete zone "${z.name}"?`)) return;
    await deleteZone(id);
    if (selectedZoneId === id) setSelectedZoneId(null);
  };

  const handleToggleVisibility = (id: number) => {
    setHiddenZoneIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectZone = (zone: GeoOpsZone) => {
    setSelectedZoneId(zone.id);
    if (zone.geometry) setFitGeometry(zone.geometry);
  };

  // Quick status update for business
  const handleQuickStatusUpdate = async (org: MSOrganization, newStatus: string) => {
    await updateOrganization(org.id, { opStatus: newStatus, lastVerified: new Date().toISOString() });
    notifications.show({ message: `${org.name} → ${newStatus}`, color: 'green' });
  };

  // Generate status check for all businesses in zone
  const handleCreateZoneStatusCheck = async () => {
    if (!activeIncident) return;
    setStatusCheckCreating(true);
    try {
      const inZoneIds = Array.from(orgsInZone);
      if (inZoneIds.length === 0) {
        notifications.show({ message: 'No businesses with coordinates found in active zones', color: 'yellow' });
        setStatusCheckCreating(false);
        return;
      }
      await createStatusCheck({
        incidentId: String(activeIncident.id),
        title: statusCheckTitle || `Impact Zone Status Check — ${new Date().toLocaleDateString()}`,
        question: statusCheckQuestion,
        targetType: 'specific',
        targetOrgs: inZoneIds.join(','),
        status: 'open',
        authorName: user?.fullName || user?.email || '',
        createdAt: new Date().toISOString(),
      });
      notifications.show({ message: `Status check created for ${inZoneIds.length} businesses`, color: 'green' });
      setShowStatusCheckModal(false);
      setStatusCheckTitle('');
    } catch {
      notifications.show({ message: 'Failed to create status check', color: 'red' });
    } finally {
      setStatusCheckCreating(false);
    }
  };

  // Add businesses in zone to a MainStreet list
  const handleAddToList = async () => {
    const inZoneIds = Array.from(orgsInZone);
    if (inZoneIds.length === 0) {
      notifications.show({ message: 'No businesses in active zones', color: 'yellow' });
      return;
    }
    setListCreating(true);
    try {
      if (listMode === 'new') {
        await createOrgList({
          name: newListName.trim() || `Zone Businesses — ${new Date().toLocaleDateString()}`,
          desc: newListDesc.trim(),
          orgIds: inZoneIds.join(','),
          createdAt: new Date().toISOString(),
        });
        notifications.show({ message: `List "${newListName || 'Zone Businesses'}" created with ${inZoneIds.length} businesses`, color: 'green' });
      } else if (selectedListId) {
        const existing = orgLists.find(l => String(l.id) === selectedListId);
        if (existing) {
          const existingIds = new Set(existing.orgIds.split(',').filter(Boolean).map(Number));
          inZoneIds.forEach(id => existingIds.add(id));
          await updateOrgList(existing.id, { orgIds: Array.from(existingIds).join(',') });
          const added = inZoneIds.filter(id => !existing.orgIds.split(',').includes(String(id))).length;
          notifications.show({ message: `Added ${added} businesses to "${existing.name}"`, color: 'green' });
        }
      }
      setShowListModal(false);
      setNewListName('');
      setNewListDesc('');
      setSelectedListId(null);
    } catch {
      notifications.show({ message: 'Failed to update list', color: 'red' });
    } finally {
      setListCreating(false);
    }
  };

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray"><IconAlertTriangle size={30} /></ThemeIcon>
          <Title order={3} c="black">No Incident Selected</Title>
          <Text size="sm" c="dimmed">Select an incident to use the map editor.</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Paper p="xs" style={{ borderBottom: '1px solid #e5e7eb', zIndex: 1000, flexShrink: 0 }}>
        <Group justify="space-between">
          <Group gap="xs">
            <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/geoops')}>Back</Button>
            <Text size="sm" fw={600}>Map Editor</Text>
            <Badge size="sm" variant="light">{activeIncident.name}</Badge>
            {/* Address search */}
            <div style={{ position: 'relative', width: 280 }}>
              <TextInput size="xs" placeholder="Search address..."
                leftSection={<IconSearch size={14} />}
                value={searchQuery}
                onChange={e => handleSearchInput(e.currentTarget.value)}
                onKeyDown={e => { if (e.key === 'Escape') setShowResults(false); }}
                rightSection={searching ? <Loader size={12} /> : null}
              />
              {showResults && searchResults.length > 0 && (
                <Paper withBorder shadow="md" style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 160,
                  maxHeight: 200, overflow: 'auto',
                }}>
                  {searchResults.map((r, i) => (
                    <div key={i} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}
                      onMouseDown={() => handleSelectAddress(r)}>
                      {r.display_name}
                    </div>
                  ))}
                </Paper>
              )}
            </div>
          </Group>

          <Group gap="xs">
            {/* Layer toggles */}
            <Tooltip label="Data Layers">
              <ActionIcon size="md" variant={showLayerPanel ? 'filled' : 'light'}
                color={showLayerPanel ? 'blue' : 'gray'}
                onClick={() => setShowLayerPanel(!showLayerPanel)}>
                <IconLayersLinked size={16} />
              </ActionIcon>
            </Tooltip>

            {/* Status check for zone */}
            {orgsInZone.size > 0 && (
              <Tooltip label={`Status check for ${orgsInZone.size} businesses in zone`}>
                <Button size="xs" variant="light" color="red"
                  leftSection={<IconClipboardCheck size={14} />}
                  onClick={() => setShowStatusCheckModal(true)}>
                  Status Check ({orgsInZone.size})
                </Button>
              </Tooltip>
            )}
            {orgsInZone.size > 0 && (
              <Tooltip label={`Add ${orgsInZone.size} businesses to a MainStreet list`}>
                <Button size="xs" variant="light" color="cyan"
                  leftSection={<IconList size={14} />}
                  onClick={() => setShowListModal(true)}>
                  Add to List ({orgsInZone.size})
                </Button>
              </Tooltip>
            )}

            {showPois && poisInZone.length > 0 && (
              <Tooltip label={`${poisInZone.length} POIs caught inside active zones — see panel`}>
                <Badge color="violet" variant="filled" size="lg" leftSection={<IconMapPin size={11} />}>
                  {poisInZone.length} POIs in zone
                </Badge>
              </Tooltip>
            )}

            <Divider orientation="vertical" />

            {canEdit && (
              <Group gap={4}>
                {drawMode !== 'idle' && (
                  <Badge color="blue" variant="light" size="sm">
                    {drawMode === 'polygon' ? 'Click vertices, dbl-click finish' :
                     drawMode === 'polyline' ? 'Click points, dbl-click finish' :
                     drawMode === 'corridor' ? 'Click points along the line, dbl-click finish' :
                     'Click center, click radius'}
                  </Badge>
                )}

                {/* Live measurement readout */}
                {drawMode !== 'idle' && liveMeasure && (() => {
                  const parts: string[] = [];
                  if (liveMeasure.radiusMeters != null && liveMeasure.radiusMeters > 0) {
                    parts.push(`r = ${formatDistance(liveMeasure.radiusMeters)}`);
                  }
                  if (liveMeasure.lengthMeters != null && liveMeasure.lengthMeters > 0) {
                    parts.push(`length: ${formatDistance(liveMeasure.lengthMeters)}`);
                  }
                  if (liveMeasure.areaSqMeters != null && liveMeasure.areaSqMeters > 0) {
                    parts.push(`area: ${formatArea(liveMeasure.areaSqMeters)}`);
                  }
                  if (liveMeasure.vertexCount != null && liveMeasure.vertexCount > 0 && (drawMode === 'polygon' || drawMode === 'polyline' || drawMode === 'corridor')) {
                    parts.push(`${liveMeasure.vertexCount} pts`);
                  }
                  return parts.length > 0 ? (
                    <Badge color="orange" variant="filled" size="sm">{parts.join(' · ')}</Badge>
                  ) : null;
                })()}

                {/* Corridor buffer width slider */}
                {drawMode === 'corridor' && (
                  <Group gap={4}>
                    <Text size="xs" c="dimmed">Buffer:</Text>
                    <Select
                      size="xs"
                      w={110}
                      value={String(corridorBufferM)}
                      onChange={v => setCorridorBufferM(Number(v) || 402)}
                      data={[
                        { value: '50', label: '50 m' },
                        { value: '100', label: '100 m' },
                        { value: '161', label: '0.1 mi' },
                        { value: '402', label: '0.25 mi' },
                        { value: '805', label: '0.5 mi' },
                        { value: '1609', label: '1 mi' },
                        { value: '3219', label: '2 mi' },
                      ]}
                    />
                  </Group>
                )}

                <Tooltip label="Draw Polygon">
                  <ActionIcon size="md" variant={drawMode === 'polygon' ? 'filled' : 'light'}
                    color={drawMode === 'polygon' ? 'blue' : 'gray'}
                    onClick={() => setDrawMode(drawMode === 'polygon' ? 'idle' : 'polygon')}>
                    <IconPolygon size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Draw Line">
                  <ActionIcon size="md" variant={drawMode === 'polyline' ? 'filled' : 'light'}
                    color={drawMode === 'polyline' ? 'blue' : 'gray'}
                    onClick={() => setDrawMode(drawMode === 'polyline' ? 'idle' : 'polyline')}>
                    <IconLine size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Draw Circle">
                  <ActionIcon size="md" variant={drawMode === 'circle' ? 'filled' : 'light'}
                    color={drawMode === 'circle' ? 'blue' : 'gray'}
                    onClick={() => setDrawMode(drawMode === 'circle' ? 'idle' : 'circle')}>
                    <IconCircle size={16} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Draw Buffered Line (Corridor) — e.g. burst pipe with .25 mi radius">
                  <ActionIcon size="md" variant={drawMode === 'corridor' ? 'filled' : 'light'}
                    color={drawMode === 'corridor' ? 'blue' : 'gray'}
                    onClick={() => setDrawMode(drawMode === 'corridor' ? 'idle' : 'corridor')}>
                    <IconRoute size={16} />
                  </ActionIcon>
                </Tooltip>
                {drawMode !== 'idle' && (
                  <ActionIcon size="md" variant="light" color="red" onClick={() => { setDrawMode('idle'); setLiveMeasure(null); }}>
                    <IconX size={16} />
                  </ActionIcon>
                )}
              </Group>
            )}
          </Group>
        </Group>
      </Paper>

      {/* Map + sidebars */}
      <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
        {/* Zone list sidebar */}
        <Paper style={{ width: 240, borderRight: '1px solid #e5e7eb', overflow: 'hidden', flexShrink: 0 }} p="xs">
          <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">Zones ({incidentZones.length})</Text>
          <ZoneListPanel
            zones={incidentZones}
            selectedZoneId={selectedZoneId}
            hiddenZoneIds={hiddenZoneIds}
            onSelect={handleSelectZone}
            onToggleVisibility={handleToggleVisibility}
            onDelete={handleDeleteZone}
            onEdit={z => { setEditingZone(z); setShowZoneForm(true); }}
            canEdit={canEdit}
          />
        </Paper>

        {/* Map */}
        <div ref={mapWrapperRef} style={{ flex: 1, position: 'relative' }}>
          {isLoading ? (
            <Center h="100%"><Loader size="md" /></Center>
          ) : (
            <MapContainer center={incidentCenter} zoom={incidentCenter[0] !== 33.749 ? 13 : 11}
              style={{ height: '100%', width: '100%' }}
              ref={(m) => { if (m) mapRef.current = m; }}>
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Street">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satellite">
                  <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution='&copy; Esri' />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Topo">
                  <TileLayer url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png" attribution='&copy; OpenTopoMap' />
                </LayersControl.BaseLayer>
              </LayersControl>

              <MapDrawControls drawMode={drawMode} />
              <ZoneMapOverlay zones={visibleZones} onZoneClick={handleSelectZone} />
              <DrawingController
                mode={drawMode}
                corridorBufferMeters={corridorBufferM}
                onComplete={handleDrawComplete}
                onCancel={handleDrawCancel}
                onMeasureUpdate={setLiveMeasure}
              />

              {/* Active incident location pin */}
              <IncidentLocationMarker incident={activeIncident} />
              <FitToZone geometry={fitGeometry} />

              {/* ── Business markers ────────────────────── */}
              {orgMarkers.map(({ org, coords }) => {
                const inZone = orgsInZone.has(org.id);
                const color = SECTOR_COLORS[org.sectorType] || '#0891b2';
                return (
                  <CircleMarker key={`biz-${org.id}`} center={[coords.lat, coords.lng]} radius={7}
                    pathOptions={{
                      fillColor: color, fillOpacity: 0.9, weight: inZone ? 3 : 1.5,
                      color: inZone ? '#DC2626' : '#fff',
                    }}>
                    <Popup minWidth={240} maxWidth={300}>
                      <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>
                        <Text size="sm" fw={700} mb={2}>{org.name || '(unnamed)'}</Text>
                        {org.sectorType && <Badge size="xs" variant="light" color="cyan" mb={4}>{org.sectorType}</Badge>}
                        {inZone && <Badge size="xs" color="red" variant="light" ml={4} mb={4}>In Zone</Badge>}

                        {org.primaryContact && (
                          <Group gap={4} mb={2}>
                            <IconBuilding size={12} color="#888" />
                            <Text size="xs">{org.primaryContact}</Text>
                          </Group>
                        )}
                        {org.phone && (
                          <Group gap={4} mb={2}>
                            <IconPhone size={12} color="#888" />
                            <Text size="xs" component="a" href={`tel:${org.phone}`} c="blue">{formatPhone(org.phone)}</Text>
                          </Group>
                        )}
                        {org.email && (
                          <Group gap={4} mb={2}>
                            <IconMail size={12} color="#888" />
                            <Text size="xs" component="a" href={`mailto:${org.email}`} c="blue">{org.email}</Text>
                          </Group>
                        )}
                        {org.address && <Text size="xs" c="dimmed" mb={4}>{org.address}{org.city ? `, ${org.city}` : ''}</Text>}

                        <Text size="xs" c="dimmed" mb={4}>
                          Status: <Badge size="xs" variant="light"
                            color={org.opStatus === 'operational' ? 'green' : org.opStatus === 'limited' ? 'yellow' : 'red'}>
                            {org.opStatus || 'unknown'}
                          </Badge>
                        </Text>

                        <Group gap={4} mt={4}>
                          <Button size="compact-xs" variant="light" color="green"
                            onClick={() => handleQuickStatusUpdate(org, 'operational')}>Operational</Button>
                          <Button size="compact-xs" variant="light" color="yellow"
                            onClick={() => handleQuickStatusUpdate(org, 'limited')}>Limited</Button>
                          <Button size="compact-xs" variant="light" color="red"
                            onClick={() => handleQuickStatusUpdate(org, 'closed_damage')}>Damaged</Button>
                        </Group>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}

              {/* ── Shelter markers ─────────────────────── */}
              {shelterMarkers.map(({ shelter, lat, lng }) => (
                <CircleMarker key={`sh-${shelter.Rid}`} center={[lat, lng]} radius={9}
                  pathOptions={{
                    fillColor: (SHELTER_STATUS_COLORS as Record<string, string>)[shelter.status] || '#888',
                    fillOpacity: 0.9, weight: 2, color: '#fff',
                  }}>
                  <Popup>
                    <div style={{ fontSize: 13 }}>
                      <Text size="sm" fw={700} mb={2}>{shelter.name}</Text>
                      <Badge size="xs" variant="light" mb={4}
                        color={shelter.status === 'open' ? 'green' : shelter.status === 'closed' ? 'red' : 'gray'}>
                        {shelter.status}
                      </Badge>
                      <Text size="xs">Capacity: {shelter.currentPopulation}/{shelter.capacity}</Text>
                      {shelter.address && <Text size="xs" c="dimmed">{shelter.address}</Text>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}

              {/* ── POI markers (HERE Browse) ───────────── */}
              {showPois && pois.map(p => {
                const inZone = incidentZones.some(z => z.geometry && isInsideZone(p.lat, p.lng, z.geometry));
                return (
                  <CircleMarker key={`poi-${p.id}`} center={[p.lat, p.lng]} radius={6}
                    pathOptions={{
                      fillColor: inZone ? '#7c3aed' : '#a78bfa',
                      fillOpacity: 0.85, weight: inZone ? 3 : 1.5,
                      color: inZone ? '#DC2626' : '#fff',
                    }}>
                    <Popup minWidth={240}>
                      <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>
                        <Group gap={4} mb={2}>
                          <IconMapPin size={12} color="#7c3aed" />
                          <Text size="sm" fw={700}>{p.title}</Text>
                        </Group>
                        {p.categoryName && (
                          <Badge size="xs" variant="filled" color="violet" mb={4}>{p.categoryName}</Badge>
                        )}
                        {inZone && <Badge size="xs" color="red" variant="light" ml={4} mb={4}>In Zone</Badge>}
                        {p.address && <Text size="xs" c="dimmed" mb={2}>{p.address}</Text>}
                        {p.phone && (
                          <Group gap={4} mb={2}>
                            <IconPhone size={11} color="#888" />
                            <Text size="xs" component="a" href={`tel:${p.phone}`} c="blue">{formatPhone(p.phone)}</Text>
                          </Group>
                        )}
                        {p.allCategories && p.allCategories.length > 1 && (
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #eee' }}>
                            <Text size="xs" c="dimmed" mb={2}>All HERE categories:</Text>
                            <Group gap={3} wrap="wrap">
                              {p.allCategories.map(c => (
                                <Badge key={c.id} size="xs" variant="light" color="gray"
                                  style={{ fontWeight: c.primary ? 700 : 400 }}>
                                  {c.name}{c.primary ? ' ★' : ''}
                                </Badge>
                              ))}
                            </Group>
                          </div>
                        )}
                        <Text size="xs" c="dimmed" mt={4}>Source: HERE Maps</Text>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}

              {/* ── Assessment markers ──────────────────── */}
              {assessmentMarkers.map(a => (
                <CircleMarker key={`asmt-${a.id}`} center={[a.latitude!, a.longitude!]} radius={5}
                  pathOptions={{
                    fillColor: SEVERITY_COLORS[a.damageCategory || 'affected'] || '#888',
                    fillOpacity: 0.8, weight: 1, color: '#fff',
                  }}>
                  <Popup>
                    <div style={{ fontSize: 13 }}>
                      <Text size="sm" fw={700} mb={2}>{a.address || `Assessment #${a.id}`}</Text>
                      <Badge size="xs" variant="light"
                        color={a.damageCategory === 'destroyed' ? 'red' : a.damageCategory === 'major' ? 'orange' : 'green'}>
                        {a.damageCategory || 'assessed'}
                      </Badge>
                      {a.assessmentType && <Text size="xs" c="dimmed" mt={2}>Type: {a.assessmentType}</Text>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          )}

          {/* Layer panel overlay — z-index 150 keeps it above leaflet controls (100)
              but below Mantine Modal (200) so it doesn't bleed through dialogs */}
          {showLayerPanel && (
            <Paper withBorder shadow="md" p="sm" style={{
              position: 'absolute', top: 8, left: 8, zIndex: 150,
              width: 220, background: 'white',
            }}>
              <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="xs">Data Layers</Text>
              <Stack gap="xs">
                <Switch size="xs" label={`Businesses${showBusinesses ? ` (${orgMarkers.length})` : ''}`}
                  checked={showBusinesses} onChange={e => setShowBusinesses(e.currentTarget.checked)}
                  color="cyan" />
                <Switch size="xs" label={`Shelters${showShelters ? ` (${shelterMarkers.length})` : ''}`}
                  checked={showShelters} onChange={e => setShowShelters(e.currentTarget.checked)}
                  color="green" />
                <Switch size="xs" label={`Assessments${showAssessments ? ` (${assessmentMarkers.length})` : ''}`}
                  checked={showAssessments} onChange={e => setShowAssessments(e.currentTarget.checked)}
                  color="orange" />
                <Switch size="xs" label={`POIs (HERE)${showPois ? ` (${pois.length})` : ''}`}
                  checked={showPois} onChange={e => setShowPois(e.currentTarget.checked)}
                  color="violet" />
              </Stack>
              {showPois && (
                <Stack gap={4} mt={6}>
                  <Button size="compact-xs" variant="light" color="violet"
                    leftSection={<IconRefresh size={11} />}
                    loading={poiLoading}
                    onClick={fetchPoisInBounds}>
                    Reload in view
                  </Button>
                  <Button size="compact-xs" variant="filled" color="violet"
                    leftSection={<IconLayersLinked size={11} />}
                    loading={poiLoading}
                    disabled={incidentZones.length === 0}
                    onClick={scanAllZones}>
                    Scan all {incidentZones.length} zone{incidentZones.length !== 1 ? 's' : ''}
                  </Button>
                </Stack>
              )}
              {showBusinesses && orgsInZone.size > 0 && (
                <>
                  <Divider my="xs" />
                  <Text size="xs" c="red" fw={500}>{orgsInZone.size} businesses in active zones</Text>
                </>
              )}
              {showPois && poisInZone.length > 0 && (
                <>
                  <Divider my="xs" />
                  <Text size="xs" c="violet" fw={600} mb={4}>
                    {poisInZone.length} POI{poisInZone.length !== 1 ? 's' : ''} captured across zones
                  </Text>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {poisInZone.slice(0, 30).map(p => (
                      <div key={p.id} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                        <Text size="xs" fw={500} lineClamp={1}>{p.title}</Text>
                        {p.categoryName && (
                          <Text size="xs" c="dimmed" lineClamp={1}>{p.categoryName}</Text>
                        )}
                        <Text size="xs" c="violet" lineClamp={1}>in: {p.zoneNames.join(', ')}</Text>
                      </div>
                    ))}
                    {poisInZone.length > 30 && (
                      <Text size="xs" c="dimmed" mt={4}>+{poisInZone.length - 30} more…</Text>
                    )}
                  </div>
                </>
              )}
            </Paper>
          )}
        </div>
      </div>

      {/* Zone form modal */}
      <ZoneForm
        opened={showZoneForm}
        onClose={() => { setShowZoneForm(false); setEditingZone(null); setDrawnGeometry(null); }}
        onSave={editingZone ? handleEditZone : handleSaveNewZone}
        initialData={editingZone || undefined}
        title={editingZone ? `Edit: ${editingZone.name}` : 'New Zone'}
        drawnGeometry={drawnGeometry}
      />

      {/* Status check modal */}
      <Modal opened={showStatusCheckModal} onClose={() => setShowStatusCheckModal(false)}
        title="Generate Status Check for Zone" size="md">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            This will create a status check targeting <strong>{orgsInZone.size} businesses</strong> located inside active zones.
          </Text>
          <TextInput label="Title" placeholder="Impact Zone Status Check"
            value={statusCheckTitle}
            onChange={e => setStatusCheckTitle(e.currentTarget.value)} />
          <Textarea label="Question" rows={3}
            value={statusCheckQuestion}
            onChange={e => setStatusCheckQuestion(e.currentTarget.value)} />
          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => setShowStatusCheckModal(false)}>Cancel</Button>
            <Button color="red" onClick={handleCreateZoneStatusCheck} loading={statusCheckCreating}>
              Create Status Check
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add to list modal */}
      <Modal opened={showListModal} onClose={() => setShowListModal(false)}
        title="Add Zone Businesses to List" size="md">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            <strong>{orgsInZone.size} businesses</strong> are inside active zones. Add them to a MainStreet organization list.
          </Text>

          <SegmentedControl fullWidth size="xs" data={[
            { value: 'new', label: 'Create New List' },
            { value: 'existing', label: 'Add to Existing' },
          ]} value={listMode} onChange={v => setListMode(v as 'new' | 'existing')} />

          {listMode === 'new' ? (
            <>
              <TextInput label="List Name" placeholder="e.g., Impact Zone — Dunwoody Businesses"
                value={newListName} onChange={e => setNewListName(e.currentTarget.value)} />
              <Textarea label="Description (optional)" rows={2}
                value={newListDesc} onChange={e => setNewListDesc(e.currentTarget.value)} />
            </>
          ) : (
            <Select label="Select List" placeholder="Choose an existing list..."
              data={orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length} orgs)` }))}
              value={selectedListId} onChange={setSelectedListId}
              nothingFoundMessage="No lists yet — create one first" />
          )}

          {/* Preview */}
          <Paper p="xs" withBorder style={{ maxHeight: 150, overflow: 'auto' }}>
            <Text size="xs" fw={600} mb={4}>Businesses to add:</Text>
            {Array.from(orgsInZone).map(id => {
              const org = organizations.find(o => o.id === id);
              return org ? (
                <Text key={id} size="xs" c="dimmed">• {org.name}{org.primaryContact ? ` — ${org.primaryContact}` : ''}</Text>
              ) : null;
            })}
          </Paper>

          <Group justify="flex-end" mt="sm">
            <Button variant="light" onClick={() => setShowListModal(false)}>Cancel</Button>
            <Button color="cyan" onClick={handleAddToList} loading={listCreating}
              disabled={listMode === 'existing' && !selectedListId}>
              {listMode === 'new' ? 'Create List' : 'Add to List'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
